// Admin: upload a video straight into an exercise slot (2026-11-02). Backend: backend/exercise-slots.js.
// Same pattern as TrainerVideos.jsx: makeExerciseSlotVideo({...}) is called once and returns the piece of the screen.
// Preview plays the video exactly as a Rebuilder gets it, from the address saved on the exercise.
import React, { useState, useRef } from 'react';

const fmtSize = b => (b >= 1024 * 1024 * 1024 ? (b / 1024 / 1024 / 1024).toFixed(2) + ' GB' : (b / 1024 / 1024).toFixed(1) + ' MB');
const typeOf = file => String(file.type || '').split(';')[0] || (/\.mov$/i.test(file.name || '') ? 'video/quicktime' : /\.webm$/i.test(file.name || '') ? 'video/webm' : 'video/mp4');

export default function makeExerciseSlotVideo({ T, Btn, api, ui }) {
  const { authHeader } = ui;
  // Sends the file in pieces (each piece is one request), with retries. onProgress(0..1).
  async function uploadVideo(exerciseId, file, onProgress) {
    const { uploadId, chunkBytes, chunks } = await api(`/api/admin/exercises/${exerciseId}/video`, 'POST', { size: file.size, type: typeOf(file) });
    try {
      for (let i = 0; i < chunks; i++) {
        const piece = file.slice(i * chunkBytes, Math.min(file.size, (i + 1) * chunkBytes));
        let done = false, lastErr = '';
        for (let t = 0; t < 4 && !done; t++) {
          try {
            const r = await fetch(`/api/admin/exercise-slot-uploads/${uploadId}/chunks/${i}`, { method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/octet-stream' }, body: piece });
            if (r.ok) done = true; else { const d = await r.json().catch(() => ({})); lastErr = d.error || 'Upload failed'; if (r.status < 500 && r.status !== 409) break; }
          } catch (e) { lastErr = 'The connection dropped. Trying again...'; }
          if (!done) await new Promise(res => setTimeout(res, 1500 * (t + 1)));
        }
        if (!done) throw new Error(lastErr || 'Upload failed');
        onProgress((i + 1) / chunks);
      }
      return await api(`/api/admin/exercise-slot-uploads/${uploadId}/finish`, 'POST', {});
    } catch (e) {
      fetch(`/api/admin/exercise-slot-uploads/${uploadId}`, { method: 'DELETE', headers: authHeader() }).catch(() => {});
      throw e;
    }
  }

  // ex: the library row. job: what the server is doing with a new file, if anything. shared: { name, hasClip } when this name can borrow another clip.
  function Slot({ ex, job, shared, maxBytes, onChanged }) {
    const input = useRef(null);
    const [progress, setProgress] = useState(null), [err, setErr] = useState(''), [open, setOpen] = useState(false);
    const pick = async e => {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      setErr('');
      if (maxBytes && file.size > maxBytes) return setErr(`That video is ${fmtSize(file.size)}. The limit is ${fmtSize(maxBytes)}. Please trim it.`);
      setProgress(0);
      try { await uploadVideo(ex.id, file, setProgress); onChanged(); }
      catch (e2) { setErr(e2.message); }
      finally { setProgress(null); }
    };
    const busy = progress !== null;
    return (
      <div style={{ flexBasis: '100%', borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input ref={input} type="file" aria-label={`Video file for ${ex.name}`} accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm" onChange={pick} style={{ display: 'none' }} />
          <Btn size="sm" variant="outline" disabled={busy} onClick={() => input.current && input.current.click()}>{ex.video_url ? 'Replace video' : 'Upload video'}</Btn>
          {ex.video_url && <Btn size="sm" variant="ghost" onClick={() => setOpen(o => !o)}>{open ? 'Close preview' : 'Preview'}</Btn>}
          {busy && <span style={{ fontSize: 12, color: T.gray }}>Uploading {Math.round(progress * 100)}%</span>}
          {!busy && job && job.status === 'processing' && <span style={{ fontSize: 12, color: T.gray }}>Processing the new video. {ex.video_url ? 'The current one keeps playing until it is ready.' : ''}</span>}
          {!busy && !job && !ex.video_url && shared && <span style={{ fontSize: 12, color: T.gray }}>Shares the clip from {shared.name}{shared.hasClip ? ', which plays here' : ' once that has one'}. Upload here only to give this name its own clip.</span>}
        </div>
        {!busy && job && job.message && <p style={{ fontSize: 13, color: T.danger, marginTop: 8 }}>{job.message}</p>}
        {err && <p style={{ fontSize: 13, color: T.danger, marginTop: 8 }}>{err}</p>}
        {open && ex.video_url && <video key={ex.video_url} src={ex.video_url} controls playsInline preload="metadata" style={{ display: 'block', width: '100%', maxWidth: 480, borderRadius: 8, marginTop: 10, background: '#000' }} />}
      </div>
    );
  }
  return { Slot };
}
